// =============================================================================
// Fundraising Types (Phase II)
// =============================================================================
// Type definitions for CRM and fundraising functionality
// =============================================================================

import type { PropertyType, InvestorProfileType } from './index';

// =============================================================================
// Enums
// =============================================================================

export type LeadSource =
  | 'REFERRAL'
  | 'WEBSITE'
  | 'EVENT'
  | 'BROKER'
  | 'LINKEDIN'
  | 'COLD_OUTREACH'
  | 'EXISTING_INVESTOR'
  | 'OTHER';

export type PipelineStage =
  | 'LEAD'
  | 'QUALIFIED'
  | 'DATA_ROOM'
  | 'INTERESTED'
  | 'SOFT_COMMIT'
  | 'HARD_COMMIT'
  | 'DOCS_SENT'
  | 'DOCS_SIGNED'
  | 'FUNDED'
  | 'CONVERTED'
  | 'PASSED'
  | 'INACTIVE';

export type AccreditationStatus =
  | 'NOT_VERIFIED'
  | 'SELF_CERTIFIED'
  | 'DOCUMENT_VERIFIED'
  | 'THIRD_PARTY_VERIFIED'
  | 'EXPIRED';

export type OfferingStatus =
  | 'DRAFT'
  | 'OPEN'
  | 'SOFT_CLOSED'
  | 'CLOSED'
  | 'CANCELLED';

export type SubscriptionStatus =
  | 'INTERESTED'
  | 'SOFT_COMMIT'
  | 'HARD_COMMIT'
  | 'DOCS_SENT'
  | 'DOCS_VIEWED'
  | 'DOCS_SIGNED'
  | 'FUNDING_PENDING'
  | 'FUNDED'
  | 'CONVERTED'
  | 'CANCELLED'
  | 'EXPIRED';

export type CommitmentType = 'SOFT' | 'HARD';

export type SignatureStatus =
  | 'CREATED'
  | 'SENT'
  | 'DELIVERED'
  | 'VIEWED'
  | 'SIGNED'
  | 'DECLINED'
  | 'VOIDED'
  | 'EXPIRED';

export type CampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'CANCELLED';

export type ContactActivityType =
  | 'NOTE_ADDED'
  | 'EMAIL_SENT'
  | 'EMAIL_OPENED'
  | 'EMAIL_CLICKED'
  | 'CALL_LOGGED'
  | 'MEETING_SCHEDULED'
  | 'MEETING_COMPLETED'
  | 'STAGE_CHANGED'
  | 'TAG_ADDED'
  | 'TAG_REMOVED'
  | 'DATA_ROOM_GRANTED'
  | 'DATA_ROOM_ACCESSED'
  | 'DOCUMENT_VIEWED'
  | 'DOCUMENT_DOWNLOADED'
  | 'INTEREST_INDICATED'
  | 'SUBSCRIPTION_CREATED'
  | 'DOCS_SENT'
  | 'DOCS_SIGNED'
  | 'FUNDING_RECEIVED'
  | 'CONVERTED_TO_INVESTOR'
  | 'PORTAL_ACCESS_GRANTED'
  | 'PORTAL_ACCESS_REVOKED';

// =============================================================================
// Contact Types
// =============================================================================

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  leadSource: LeadSource;
  referredBy?: string;
  referralNotes?: string;
  pipelineStage: PipelineStage;
  stageChangedAt: string;
  accreditationStatus?: AccreditationStatus;
  accreditationMethod?: string;
  accreditationDate?: string;
  accreditationExpires?: string;
  investmentCapacity?: string;
  propertyInterests: PropertyType[];
  tags: string[];
  userId?: string;
  portalAccessGranted?: string;
  emailUnsubscribed: boolean;
  unsubscribedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  convertedAt?: string;
  convertedToUserId?: string;
}

export interface ContactWithSubscriptions extends Contact {
  subscriptions: SubscriptionSummary[];
}

export interface CreateContactInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  leadSource: LeadSource;
  referredBy?: string;
  referralNotes?: string;
  accreditationStatus?: AccreditationStatus;
  accreditationMethod?: string;
  investmentCapacity?: string;
  propertyInterests?: PropertyType[];
  tags?: string[];
  notes?: string;
}

export interface UpdateContactInput extends Partial<CreateContactInput> {
  id: string;
}

// =============================================================================
// Contact Activity Types
// =============================================================================

export interface ContactActivity {
  id: string;
  contactId: string;
  type: ContactActivityType;
  description: string;
  metadata?: Record<string, any>;
  createdById?: string;
  createdAt: string;
}

// =============================================================================
// Subscription Types
// =============================================================================

export interface SubscriptionSummary {
  id: string;
  status: SubscriptionStatus;
  commitmentAmount: number;
  deal: {
    id: string;
    name: string;
  };
}

export interface Subscription {
  id: string;
  contactId: string;
  dealId: string;
  status: SubscriptionStatus;
  statusChangedAt: string;
  commitmentAmount: number;
  commitmentType: CommitmentType;
  commitmentDate?: string;
  entityType?: InvestorProfileType;
  entityName?: string;
  entityState?: string;
  signatureRequestId?: string;
  signatureStatus?: SignatureStatus;
  docsSentAt?: string;
  docsSignedAt?: string;
  signedDocumentPath?: string;
  fundingMethod?: 'ACH' | 'CHECK' | 'WIRE';
  fundingInstructions?: string;
  fundedAmount?: number;
  fundedAt?: string;
  fundingReference?: string;
  fundingConfirmedBy?: string;
  convertedAt?: string;
  investmentId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionWithDetails extends Subscription {
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string | null;
  };
  deal: {
    id: string;
    name: string;
    status: string;
  };
}

export interface CreateSubscriptionInput {
  contactId: string;
  dealId: string;
  commitmentAmount: number;
  commitmentType?: CommitmentType;
  entityType?: InvestorProfileType;
  entityName?: string;
  entityState?: string;
  notes?: string;
}

export interface UpdateSubscriptionInput {
  status?: SubscriptionStatus;
  commitmentAmount?: number;
  commitmentType?: CommitmentType;
  entityType?: InvestorProfileType;
  entityName?: string;
  entityState?: string;
  fundedAmount?: number;
  fundingReference?: string;
  notes?: string;
}

export interface SubscriptionStats {
  totalSubscriptions: number;
  totalCommitted: number;
  totalFunded: number;
  byStatus: Record<string, { count: number; amount: number }>;
}

// =============================================================================
// Pipeline Types
// =============================================================================

export interface PipelineStats {
  stage: PipelineStage;
  count: number;
  totalCommitment: number;
}

export interface PipelineColumn {
  stage: PipelineStage;
  contacts: ContactWithSubscriptions[];
}

// =============================================================================
// List Response Types
// =============================================================================

export interface ContactListResponse {
  contacts: ContactWithSubscriptions[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ContactFilters {
  search?: string;
  pipelineStage?: PipelineStage | PipelineStage[];
  leadSource?: LeadSource | LeadSource[];
  tags?: string[];
  hasPortalAccess?: boolean;
  emailUnsubscribed?: boolean;
}

// =============================================================================
// Display Helpers
// =============================================================================

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  LEAD: 'Lead',
  QUALIFIED: 'Qualified',
  DATA_ROOM: 'Data Room',
  INTERESTED: 'Interested',
  SOFT_COMMIT: 'Soft Commit',
  HARD_COMMIT: 'Hard Commit',
  DOCS_SENT: 'Docs Sent',
  DOCS_SIGNED: 'Docs Signed',
  FUNDED: 'Funded',
  CONVERTED: 'Converted',
  PASSED: 'Passed',
  INACTIVE: 'Inactive',
};

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  REFERRAL: 'Referral',
  WEBSITE: 'Website',
  EVENT: 'Event',
  BROKER: 'Broker',
  LINKEDIN: 'LinkedIn',
  COLD_OUTREACH: 'Cold Outreach',
  EXISTING_INVESTOR: 'Existing Investor',
  OTHER: 'Other',
};

export const ACCREDITATION_STATUS_LABELS: Record<AccreditationStatus, string> = {
  NOT_VERIFIED: 'Not Verified',
  SELF_CERTIFIED: 'Self-Certified',
  DOCUMENT_VERIFIED: 'Document Verified',
  THIRD_PARTY_VERIFIED: 'Third-Party Verified',
  EXPIRED: 'Expired',
};

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  INTERESTED: 'Interested',
  SOFT_COMMIT: 'Soft Commit',
  HARD_COMMIT: 'Hard Commit',
  DOCS_SENT: 'Docs Sent',
  DOCS_VIEWED: 'Docs Viewed',
  DOCS_SIGNED: 'Docs Signed',
  FUNDING_PENDING: 'Funding Pending',
  FUNDED: 'Funded',
  CONVERTED: 'Converted',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

// =============================================================================
// Property Type Labels
// =============================================================================

export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  MULTIFAMILY: 'Multifamily',
  OFFICE: 'Office',
  RETAIL: 'Retail',
  INDUSTRIAL: 'Industrial',
  MIXED_USE: 'Mixed Use',
  HOTEL: 'Hotel',
  SELF_STORAGE: 'Self Storage',
  OTHER: 'Other',
};

// =============================================================================
// Deal Status Labels
// =============================================================================

export const DEAL_STATUS_LABELS: Record<string, string> = {
  FUNDRAISING: 'Fundraising',
  ACTIVE: 'Active',
  CLOSED: 'Closed',
};

/**
 * Generic formatter for any enum value - converts UPPER_SNAKE_CASE to Title Case.
 * Use as a fallback when a specific label map doesn't exist.
 */
export function formatEnumLabel(value: string): string {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// =============================================================================
// Data Room Types
// =============================================================================

export interface DataRoomAccess {
  id: string;
  contactId: string;
  dealId: string;
  grantedAt: string;
  grantedById: string;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  ndaRequired: boolean;
  ndaSignedAt: string | null;
  ndaDocumentPath: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string | null;
  };
  deal: {
    id: string;
    name: string;
    status: string;
  };
  documentViews: Array<{
    id: string;
    documentId: string;
    viewedAt: string;
    downloadedAt: string | null;
  }>;
}

export interface ContactDataRoomAccess {
  id: string;
  dealId: string;
  grantedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  ndaRequired: boolean;
  ndaSignedAt: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
  deal: {
    id: string;
    name: string;
    status: string;
    propertyType: string;
    targetRaise: number | null;
    imageUrl: string | null;
  };
}

export interface GrantAccessInput {
  contactId: string;
  dealId: string;
  expiresAt?: string | null;
  ndaRequired?: boolean;
}

export interface AvailableDeal {
  id: string;
  name: string;
  status: string;
}

// =============================================================================
// Email Campaign Types
// =============================================================================

export interface SegmentCriteria {
  tags?: string[];
  stages?: PipelineStage[];
  dealIds?: string[];
  leadSources?: string[];
}

export interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  segmentCriteria: SegmentCriteria | null;
  excludeUnsubscribed: boolean;
  status: CampaignStatus;
  scheduledAt: string | null;
  sentAt: string | null;
  totalRecipients: number;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  totalBounced: number;
  totalUnsubscribed: number;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignInput {
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  segmentCriteria?: SegmentCriteria;
  excludeUnsubscribed?: boolean;
}

export interface UpdateCampaignInput {
  name?: string;
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  segmentCriteria?: SegmentCriteria;
  excludeUnsubscribed?: boolean;
}

export interface CampaignRecipient {
  id: string;
  campaignId: string;
  contactId: string;
  sentAt: string | null;
  deliveredAt: string | null;
  bouncedAt: string | null;
  bounceReason: string | null;
  openedAt: string | null;
  openCount: number;
  clickedAt: string | null;
  clickCount: number;
  unsubscribedAt: string | null;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string | null;
  };
}

export interface CampaignStats {
  totalRecipients: number;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  totalBounced: number;
  totalUnsubscribed: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
}

export interface CampaignListResponse {
  campaigns: EmailCampaign[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CampaignPreview {
  count: number;
  contacts: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string | null;
  }>;
}

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  SENDING: 'Sending',
  SENT: 'Sent',
  CANCELLED: 'Cancelled',
};

// =============================================================================
// Conversion Types (Subscription → Investor)
// =============================================================================

export interface ConversionResult {
  success: boolean;
  userId?: string;
  investmentId?: string;
  investorProfileId?: string;
  message: string;
}

export interface ConversionInput {
  subscriptionId: string;
  ownershipPercentage?: number;
  investmentDate?: string;
  sendWelcomeEmail?: boolean;
}

export interface BulkConversionResult {
  successful: ConversionResult[];
  failed: ConversionResult[];
}

export interface CanConvertResult {
  canConvert: boolean;
  reason?: string;
  existingUser?: boolean;
}

export interface ReadyForConversionItem {
  id: string;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
  };
  deal: {
    id: string;
    name: string;
  };
  fundedAmount: number;
  fundedAt: string | null;
  hasExistingUser: boolean;
}

// =============================================================================
// Prospect Portal Types
// =============================================================================

export interface ProspectDataRoom {
  accessId: string;
  deal: {
    id: string;
    name: string;
    status: string;
    propertyType: string;
    address: string;
    city: string;
    state: string;
    location: string;
    targetRaise: number | null;
    imageUrl: string | null;
    description: string | null;
  };
  grantedAt: string;
  expiresAt: string | null;
  ndaRequired: boolean;
  ndaSignedAt: string | null;
  canAccessDocuments: boolean;
  lastAccessedAt: string | null;
  accessCount: number;
}

export interface ProspectDataRoomDocument {
  id: string;
  name: string;
  type: string;
  filePath: string;
  uploadedAt: string;
  uploadedBy: { name: string };
  viewedAt: string | null;
  downloadedAt: string | null;
}

export interface ProspectNdaStatus {
  ndaRequired: boolean;
  ndaSignedAt: string | null;
  ndaDocumentPath: string | null;
  dealName: string;
}

export interface SignNdaInput {
  signatureData?: string;
  signatureMethod?: 'DRAW' | 'TYPE' | 'UPLOAD';
  signedName: string;
  agreedToTerms: true;
}
